/**
 * Kitchen / bar production lifecycle and SLA evaluation.
 *
 * WHY one shared table with a parameterised permission prefix:
 * A grill station and a cocktail bar run the exact same process — a ticket is
 * offered, accepted, prepared, finished, picked up, delivered, and sometimes
 * returned and remade. Only the *authority* differs: a bartender holds `bar.*`,
 * a line cook holds `kitchen.*`. Duplicating the graph would guarantee the two
 * drift apart the first time someone fixes a bug in one of them, so the graph
 * is written once and the prefix is injected.
 *
 * Statuses mirror `production_tickets.status` / `production_ticket_items.status`
 * in `database/migrations/0007_production.sql`.
 */

import type { Millis } from '@sonauto/utilities';
import { StateMachine } from './state-machine.js';
import type { Transition } from './state-machine.js';

/** Production statuses exactly as constrained in the DB. */
export const PRODUCTION_STATUSES = [
  'WAITING',
  'OFFERED',
  'ACCEPTED',
  'PREPARING',
  'PAUSED',
  'WAITING_INGREDIENT',
  'COMPLETED',
  'WAITING_PICKUP',
  'PICKED_UP',
  'DELIVERED',
  'RETURNED',
  'REMAKE_REQUIRED',
  'REMAKING',
  'CANCELLED',
] as const;

/** Union of production statuses. */
export type ProductionStatus = (typeof PRODUCTION_STATUSES)[number];

/** Production events. */
export const PRODUCTION_EVENTS = [
  'OFFER',
  'ACCEPT',
  'START',
  'PAUSE',
  'RESUME',
  'MISSING_INGREDIENT',
  'INGREDIENT_RESOLVED',
  'COMPLETE',
  'PICK_UP',
  'DELIVER',
  'RETURN',
  'REQUEST_REMAKE',
  'START_REMAKE',
  'CANCEL',
] as const;

/** Union of production events. */
export type ProductionEvent = (typeof PRODUCTION_EVENTS)[number];

/**
 * Which station family a machine belongs to. This is the permission prefix and
 * matches the `kitchen.*` / `bar.*` modules in the permission catalogue.
 */
export type ProductionDomain = 'kitchen' | 'bar';

/**
 * One edge of the shared production graph, before the prefix is applied.
 *
 * `permissionSuffix` becomes `${domain}.${suffix}`; `permissionCode` is an
 * absolute code for the few edges that are NOT station authority (delivering a
 * dish is service work, cancelling a dish is order authority).
 */
interface ProductionEdge {
  readonly from: ProductionStatus;
  readonly event: ProductionEvent;
  readonly to: ProductionStatus;
  readonly permissionSuffix?: string;
  readonly permissionCode?: string;
  readonly guardName?: string;
}

/**
 * The shared production graph.
 *
 * Notable modelling decisions:
 * - OFFER is unguarded: it is the dispatcher (a Durable Object alarm), not a
 *   human, that offers a ticket to the next available cook.
 * - WAITING can go straight to ACCEPTED or PREPARING so that stations with
 *   `stations.auto_accept = 1` (a one-person bar) are not forced through an
 *   offer round nobody will tap.
 * - COMPLETED --OFFER--> WAITING_PICKUP is how a finished dish enters the
 *   runner queue: the same dispatcher that offers cooking work offers the
 *   delivery. A server may also grab it directly (COMPLETED --PICK_UP-->).
 * - RESUME and INGREDIENT_RESOLVED reuse `.start` rather than inventing
 *   `.resume`: whoever may start cooking may un-pause it, and adding permission
 *   codes that no role would ever be granted separately is noise.
 */
const PRODUCTION_EDGES: readonly ProductionEdge[] = [
  { from: 'WAITING', event: 'OFFER', to: 'OFFERED' },
  { from: 'WAITING', event: 'ACCEPT', to: 'ACCEPTED', permissionSuffix: 'accept', guardName: 'selfClaimAllowed' },
  { from: 'OFFERED', event: 'ACCEPT', to: 'ACCEPTED', permissionSuffix: 'accept' },
  // Re-offer after an offer round times out with nobody accepting.
  { from: 'OFFERED', event: 'OFFER', to: 'OFFERED' },

  { from: 'ACCEPTED', event: 'START', to: 'PREPARING', permissionSuffix: 'start' },
  { from: 'WAITING', event: 'START', to: 'PREPARING', permissionSuffix: 'start', guardName: 'stationAutoAccept' },

  { from: 'PREPARING', event: 'PAUSE', to: 'PAUSED', permissionSuffix: 'pause', guardName: 'requiresReason' },
  { from: 'PAUSED', event: 'RESUME', to: 'PREPARING', permissionSuffix: 'start' },

  // Missing stock is tracked separately from a plain pause because it must
  // raise a warehouse alert and can auto-86 the product.
  {
    from: 'PREPARING',
    event: 'MISSING_INGREDIENT',
    to: 'WAITING_INGREDIENT',
    permissionSuffix: 'pause',
    guardName: 'requiresReason',
  },
  {
    from: 'ACCEPTED',
    event: 'MISSING_INGREDIENT',
    to: 'WAITING_INGREDIENT',
    permissionSuffix: 'pause',
    guardName: 'requiresReason',
  },
  { from: 'WAITING_INGREDIENT', event: 'INGREDIENT_RESOLVED', to: 'PREPARING', permissionSuffix: 'start' },

  { from: 'PREPARING', event: 'COMPLETE', to: 'COMPLETED', permissionSuffix: 'complete' },
  { from: 'REMAKING', event: 'COMPLETE', to: 'COMPLETED', permissionSuffix: 'complete' },

  { from: 'COMPLETED', event: 'OFFER', to: 'WAITING_PICKUP' },
  { from: 'COMPLETED', event: 'PICK_UP', to: 'PICKED_UP', permissionCode: 'task.accept' },
  { from: 'WAITING_PICKUP', event: 'PICK_UP', to: 'PICKED_UP', permissionCode: 'task.accept' },
  { from: 'WAITING_PICKUP', event: 'OFFER', to: 'WAITING_PICKUP' },

  { from: 'PICKED_UP', event: 'DELIVER', to: 'DELIVERED', permissionCode: 'task.complete' },

  // A dish can come back from the table (guest rejected it) or from the pass
  // (expo rejected it). Both land in RETURNED and both need a reason for KPI
  // attribution — `production_returns.responsible_party` depends on it.
  { from: 'DELIVERED', event: 'RETURN', to: 'RETURNED', permissionSuffix: 'return', guardName: 'requiresReason' },
  { from: 'PICKED_UP', event: 'RETURN', to: 'RETURNED', permissionSuffix: 'return', guardName: 'requiresReason' },
  { from: 'WAITING_PICKUP', event: 'RETURN', to: 'RETURNED', permissionSuffix: 'return', guardName: 'requiresReason' },

  {
    from: 'RETURNED',
    event: 'REQUEST_REMAKE',
    to: 'REMAKE_REQUIRED',
    permissionSuffix: 'remake',
    guardName: 'requiresReason',
  },
  {
    from: 'DELIVERED',
    event: 'REQUEST_REMAKE',
    to: 'REMAKE_REQUIRED',
    permissionSuffix: 'remake',
    guardName: 'requiresReason',
  },
  { from: 'REMAKE_REQUIRED', event: 'START_REMAKE', to: 'REMAKING', permissionSuffix: 'remake' },

  // Cancelling a ticket is order authority, not station authority: a cook may
  // not remove a dish from the guest's bill.
  ...(
    [
      'WAITING',
      'OFFERED',
      'ACCEPTED',
      'PREPARING',
      'PAUSED',
      'WAITING_INGREDIENT',
      'REMAKE_REQUIRED',
      'RETURNED',
    ] as const
  ).map(
    (from): ProductionEdge => ({
      from,
      event: 'CANCEL',
      to: 'CANCELLED',
      permissionCode: 'order.item.cancel',
      guardName: 'requiresReason',
    }),
  ),
];

/**
 * Resolve the shared graph for one station family.
 *
 * Exported so a future station type (a dedicated dessert or packing module with
 * its own permission prefix) can be added without touching this file's graph.
 */
export function buildProductionTransitions(
  domain: ProductionDomain,
): readonly Transition<ProductionStatus, ProductionEvent>[] {
  return PRODUCTION_EDGES.map((edge) => ({
    from: edge.from,
    event: edge.event,
    to: edge.to,
    permission:
      edge.permissionCode ?? (edge.permissionSuffix === undefined ? undefined : `${domain}.${edge.permissionSuffix}`),
    guardName: edge.guardName,
  }));
}

/**
 * Kitchen station lifecycle (`kitchen.*` permissions).
 * Used by the KDS screens for GRILL / FRY / STEAM / COLD / DESSERT stations.
 */
export const kitchenMachine = new StateMachine<ProductionStatus, ProductionEvent>(
  buildProductionTransitions('kitchen'),
  { name: 'món bếp', terminalStates: ['CANCELLED'] },
);

/**
 * Bar station lifecycle (`bar.*` permissions).
 * Identical graph, different authority — see the file header.
 */
export const barMachine = new StateMachine<ProductionStatus, ProductionEvent>(buildProductionTransitions('bar'), {
  name: 'món pha chế',
  terminalStates: ['CANCELLED'],
});

/** Pick the machine for a station family without a `switch` at every call site. */
export function productionMachineFor(domain: ProductionDomain): StateMachine<ProductionStatus, ProductionEvent> {
  return domain === 'bar' ? barMachine : kitchenMachine;
}

/**
 * Where a ticket stands against its promised prep time.
 *
 * WARNING is a distinct state (not just "nearly LATE") because it is what makes
 * the system preventive: the KDS turns the card amber and the dispatcher can
 * pull in a second cook *before* the guest is kept waiting.
 */
export type SlaState = 'ON_TIME' | 'WARNING' | 'LATE';

/** Result of an SLA evaluation. */
export interface SlaEvaluation {
  readonly state: SlaState;
  /**
   * Seconds left until `slaDueAt`. Goes NEGATIVE once the ticket is late — the
   * KDS shows "-3:20" and the overdue magnitude drives escalation, so clamping
   * it at zero would throw away the only number that matters when it matters.
   */
  readonly remainingSeconds: number;
  /** How much of the SLA window is spent, in basis points (10000 = due now). */
  readonly elapsedRatioBp: number;
}

/**
 * Evaluate a ticket's SLA.
 *
 * WHY basis points for the warning threshold: `stations.sla_warning_ratio_bp`
 * stores it as an integer (default 8000 = 80%). Keeping the same unit end to
 * end means no float rounding difference between the alert the Worker raises
 * and the colour the KDS paints.
 *
 * @param now       Server clock only — never a device clock (see utilities/time).
 * @param sentAt    When the ticket was fired to the station; start of the window.
 * @param slaDueAt  `sentAt + sla_seconds`.
 * @param warnRatioBp Fraction of the window after which to warn (default 8000).
 */
export function slaState(now: Millis, sentAt: Millis, slaDueAt: Millis, warnRatioBp = 8000): SlaEvaluation {
  const remainingSeconds = Math.round((slaDueAt - now) / 1000);
  const windowMs = slaDueAt - sentAt;

  // A non-positive window means the station has no meaningful SLA configured
  // (or the clock data is broken); fall back to a pure due/not-due decision
  // rather than dividing by zero and painting the whole board red.
  if (windowMs <= 0) {
    return {
      state: now >= slaDueAt ? 'LATE' : 'ON_TIME',
      remainingSeconds,
      elapsedRatioBp: now >= slaDueAt ? 10000 : 0,
    };
  }

  const elapsedRatioBp = Math.round(((now - sentAt) / windowMs) * 10000);
  if (now >= slaDueAt) return { state: 'LATE', remainingSeconds, elapsedRatioBp };

  const safeWarnBp = Math.min(10000, Math.max(0, warnRatioBp));
  const warnAt = sentAt + Math.floor((windowMs * safeWarnBp) / 10000);
  return { state: now >= warnAt ? 'WARNING' : 'ON_TIME', remainingSeconds, elapsedRatioBp };
}

/** Statuses in which a station still owes work — the KDS "active" filter. */
export function isProductionActive(status: ProductionStatus): boolean {
  switch (status) {
    case 'WAITING':
    case 'OFFERED':
    case 'ACCEPTED':
    case 'PREPARING':
    case 'PAUSED':
    case 'WAITING_INGREDIENT':
    case 'REMAKE_REQUIRED':
    case 'REMAKING':
      return true;
    case 'COMPLETED':
    case 'WAITING_PICKUP':
    case 'PICKED_UP':
    case 'DELIVERED':
    case 'RETURNED':
    case 'CANCELLED':
      return false;
  }
}

/**
 * Does this status mean the food physically exists?
 *
 * Used by inventory: from here on, a cancellation is a real loss, matching
 * `cancelStageOf`'s AFTER_PRODUCTION branch on the order item.
 */
export function isProductionOutputRealised(status: ProductionStatus): boolean {
  switch (status) {
    case 'COMPLETED':
    case 'WAITING_PICKUP':
    case 'PICKED_UP':
    case 'DELIVERED':
    case 'RETURNED':
      return true;
    case 'WAITING':
    case 'OFFERED':
    case 'ACCEPTED':
    case 'PREPARING':
    case 'PAUSED':
    case 'WAITING_INGREDIENT':
    case 'REMAKE_REQUIRED':
    case 'REMAKING':
    case 'CANCELLED':
      return false;
  }
}
